/**
 * The embedding/repo PORT shapes the search-side semantic query path consumes.
 * Declared HERE (not imported from @domains/google) so domains/search stays
 * inside its own boundary — the app reader controller injects the real
 * @domains/google embedding facade + repos, exactly like the EmbeddingIndexer's
 * injected ports (see the `*Port` interfaces in EmbeddingIndexer.ts).
 */
import type { CacheEmbeddingsRow, CacheEmbedJobsRow } from '@data/rows/cache';

/**
 * The quantization scheme stamp the indexer writes and BOTH the read
 * (semanticRank) and index (EmbeddingIndexer) paths compare against (design
 * §8.2 of plan/semantic-search-design.md). It is the single literal carried on
 * every persisted row (`quant` in @data/rows/cache); a future scheme change
 * bumps this constant, and a stored row whose `quant` no longer matches is
 * treated as INVALID (re-embed, never convert — incompatible spaces).
 */
export const CURRENT_QUANT: CacheEmbeddingsRow['quant'] = 'int8-pervec';

/**
 * The embedding task profile (mirrors @domains/google EmbeddingProfile —
 * genai/embedding/contract.ts): `document` for corpus indexing, `query` for
 * search-time lookup. The asymmetric pair is what makes the cosine meaningful.
 */
export type EmbeddingProfile = 'document' | 'query';

/** The slice of the EmbeddingClient the semantic query path consumes. */
export interface EmbeddingClientPort {
  embed(
    texts: string[],
    opts: { profile: EmbeddingProfile; bookId?: string; interactive?: boolean; signal?: AbortSignal },
  ): Promise<{ vectors: Float32Array[] }>;
  /** Whether the client currently holds a usable configuration (API key). */
  isConfigured(): boolean;
}

/**
 * A `cache_embeddings` row as the repo READ path hands it over: the persisted
 * binary buffers are already re-wrapped as the typed-array views the cosine
 * ranking consumes (CacheEmbeddingsView in @data/repos/embeddings).
 */
export type EmbeddedRowView = Omit<CacheEmbeddingsRow, 'sections'> & {
  sections: (Omit<CacheEmbeddingsRow['sections'][number], 'vectors' | 'scales'> & {
    vectors: Int8Array;
    scales: Float32Array;
  })[];
};

/** The slice of the embeddings repo the semantic query path consumes. */
export interface EmbeddingsSourcePort {
  get(bookId: string): Promise<EmbeddedRowView | undefined>;
  /**
   * The resume journal for the book (`cache_embed_jobs`), whose per-section
   * `{href, sectionTextHash}` entries mirror what the vectors row holds — at a
   * fraction of the bytes. The embedding-progress poll counts from HERE instead
   * of deserializing every packed vector in the book once every few seconds
   * (and while the indexer is rewriting that very row). Optional so existing
   * injections/doubles that only provide `get` still satisfy the port; callers
   * fall back to the full-row read when it is absent or returns nothing.
   */
  getJob?(bookId: string): Promise<CacheEmbedJobsRow | undefined>;
}

/** The int8 per-vector quantizer port (SearchEngine.quantizeInt8PerVector). */
export type QuantizePort = (vec: Float32Array) => { vectors: Int8Array; scale: number };

/** Semantic on/off + {model, dims}, read per call from the app store (injected). */
export type SemanticConfigPort = () => { enabled: boolean; model: string; dims: number };
